/**
 * Porting Wizard - Multi-step number porting with dry run simulator
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../auth';
import './PortingWizard.css';

const STEPS = [
  { id: 1, label: 'Check' },
  { id: 2, label: 'Carrier' },
  { id: 3, label: 'Sign LOA' },
  { id: 4, label: 'Status' },
];

// Parent carriers and their MVNOs/sub-brands — Twilio often returns the
// parent network name for MVNO numbers, so we let the user correct it.
const CARRIER_FAMILY = {
  'Verizon Wireless': ['Visible', 'Straight Talk', 'TracFone', 'Total Wireless', 'Red Pocket Mobile', 'Xfinity Mobile', 'Spectrum Mobile'],
  'AT&T Mobility': ['Cricket Wireless', 'Straight Talk', 'TracFone', 'Consumer Cellular', 'Red Pocket Mobile'],
  'T-Mobile': ['Metro by T-Mobile', 'Boost Mobile', 'Mint Mobile', 'Google Fi', 'Ting', 'Republic Wireless', 'Straight Talk', 'Red Pocket Mobile'],
};

const CARRIER_INFO = {
  'AT&T Mobility': {
    acctHelp: 'Log in at att.com > Account > Account overview — account number is at the top (ends in -00001). Also on your bill.',
    pinHelp: 'Dial *PORT from your AT&T phone, or myAT&T app > Profile > People & Permissions > Transfer phone number > Request a new PIN. Expires in 4 days.',
  },
  'Verizon Wireless': {
    acctHelp: 'My Verizon app or verizon.com > Account > Account Settings > Account Settings Overview — listed below "My Profile" (ends in -00001). Also on your bill.',
    pinHelp: 'Dial #PORT from your Verizon phone to get a link, or My Verizon > Generate Number Transfer PIN. Valid 7 days, max 5 requests/day.',
  },
  'T-Mobile': {
    acctHelp: 'T-Mobile app or my.t-mobile.com > Account > Profile settings > "Account number." Also on your monthly bill.',
    pinHelp: 'T-Mobile app > More > Profile settings > Request transfer PIN, or call 611. PIN is valid for 7 days.',
  },
  'Sprint': {
    acctHelp: 'Sprint merged with T-Mobile. Log in at my.t-mobile.com with your Sprint credentials — same account flow as T-Mobile.',
    pinHelp: 'Same as T-Mobile: app or website > Profile settings > Request transfer PIN, or call 611.',
  },
  'US Cellular': {
    acctHelp: 'uscellular.com > My Account > My Profile, or check your monthly bill statement.',
    pinHelp: 'Call 611 from your US Cellular phone and request a Number Transfer PIN.',
  },
  'Cricket Wireless': {
    acctHelp: 'myCricket app or cricketwireless.com > Account > Account details. Also on your bill.',
    pinHelp: 'myCricket app > Account > Transfer PIN, or call 1-800-274-2538. May need last 4 of SSN.',
  },
  'Metro by T-Mobile': {
    acctHelp: 'mymetro.com > My Account, or visit a Metro store with photo ID. Account number is on your bill.',
    pinHelp: 'Your 8-digit Metro PIN (set at activation). Reset at mymetro.com > Account > Security, or call 611.',
  },
  'Boost Mobile': {
    acctHelp: 'my.boostmobile.com > My Account, or check your confirmation email. Account number may be your phone number.',
    pinHelp: 'Boost app > Account > Request Transfer PIN, or call 611. It\'s a 4-digit PIN separate from your account PIN.',
  },
  'Mint Mobile': {
    acctHelp: 'Mint Mobile app > Account, or mintmobile.com > Account settings. Account number is on your bill.',
    pinHelp: 'Mint Mobile app > Account > Transfer PIN, or chat with support at mintmobile.com.',
  },
  'Visible': {
    acctHelp: 'Visible app > Account, or visible.com > Account. Account number is shown on your bill (top right corner). It is NOT just your phone number.',
    pinHelp: 'Visible app > Account > Port-Out PIN > "Request via email." You\'ll receive a 6-digit PIN by email/text. Valid 7 days, max 3 requests per 24h.',
  },
  'Google Fi': {
    acctHelp: 'fi.google.com > Account > Your plan > "Port out information" section shows your account number.',
    pinHelp: 'Same page: fi.google.com > Account > Your plan > Port out information. PIN is displayed alongside account number.',
  },
  'Xfinity Mobile': {
    acctHelp: 'xfinity.com/mobile > Account > Transfer PIN page shows your account number.',
    pinHelp: 'Same page: xfinity.com/mobile > Account > Transfer PIN. Both shown together.',
  },
  'Spectrum Mobile': {
    acctHelp: 'spectrum.net > Account > Your services > Mobile. Account number is on your bill statement.',
    pinHelp: 'Call Spectrum Mobile at 1-833-224-6603 to request a transfer PIN.',
  },
  'Consumer Cellular': {
    acctHelp: 'consumercellular.com > My Account, or check your welcome letter / monthly bill.',
    pinHelp: 'Call Consumer Cellular at 1-888-345-5509 to request your transfer PIN.',
  },
  'Straight Talk': {
    acctHelp: 'straighttalk.com > My Account > Account info, or dial 611611 from your phone.',
    pinHelp: 'Call 1-877-430-2355 or chat at straighttalk.com to request your transfer PIN.',
  },
  'TracFone': {
    acctHelp: 'tracfone.com > My Account, or check your original activation email.',
    pinHelp: 'Call TracFone at 1-800-867-7183 and request your NTP (Number Transfer PIN).',
  },
  'Total Wireless': {
    acctHelp: 'totalwireless.com > My Account, or check your activation confirmation.',
    pinHelp: 'Call Total Wireless at 1-866-663-3633 to request your transfer PIN.',
  },
  'Republic Wireless': {
    acctHelp: 'republicwireless.com > Account Settings. Account number is displayed there.',
    pinHelp: 'Same page: Account Settings > Transfer Info shows your transfer PIN.',
  },
  'Ting': {
    acctHelp: 'ting.com > Account > Transfer/Port out. Account number is displayed there.',
    pinHelp: 'Same page: ting.com > Account > Transfer/Port out shows your PIN.',
  },
  'Red Pocket Mobile': {
    acctHelp: 'redpocket.com > My Lines > Account Info, or check your welcome email.',
    pinHelp: 'Call Red Pocket at 1-712-775-8777 or use chat support to request a transfer PIN.',
  },
};

const US_CARRIERS = Object.keys(CARRIER_INFO);

const STATUS_STEPS = [
  { status: 'draft', label: 'Draft Created' },
  { status: 'pending', label: 'Submitted for Review' },
  { status: 'approved', label: 'Carrier Approved' },
  { status: 'loa_signed', label: 'LOA Signed & Verified' },
  { status: 'porting', label: 'Porting In Progress' },
  { status: 'completed', label: 'Port Complete!' },
];

const PortingWizard = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [dryRun, setDryRun] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1: Portability check
  const [phoneNumber, setPhoneNumber] = useState('');
  const [portabilityResult, setPortabilityResult] = useState(null);

  // Step 2: Carrier info
  const [carrierName, setCarrierName] = useState('');
  const [detectedCarrier, setDetectedCarrier] = useState(''); // original from Twilio
  const [accountNumber, setAccountNumber] = useState('');
  const [pin, setPin] = useState('');
  const [repName, setRepName] = useState('');
  const [repEmail, setRepEmail] = useState('');
  const [repPhone, setRepPhone] = useState('');
  const [repAddress, setRepAddress] = useState('');
  const [repCity, setRepCity] = useState('');
  const [repState, setRepState] = useState('');
  const [repZip, setRepZip] = useState('');

  // Step 3: LOA signature
  const [loaSigned, setLoaSigned] = useState(false);
  const [loaTypedName, setLoaTypedName] = useState('');

  // Profile completion dialog
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  // Step 4: Request
  const [targetDate, setTargetDate] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [currentPortStatus, setCurrentPortStatus] = useState('draft');
  const [simulating, setSimulating] = useState(false);

  // Past requests
  const [pastRequests, setPastRequests] = useState([]);

  // Auto-fill from user profile
  useEffect(() => {
    if (user) {
      const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ');
      if (fullName && fullName !== 'undefined_firstname undefined_lastname') {
        setRepName(fullName);
        setLoaTypedName(fullName);
      }
      if (user.email) setRepEmail(user.email);
      if (user.targetnumber1) setRepPhone(user.targetnumber1);
      if (user.address) setRepAddress(user.address);
      if (user.city) setRepCity(user.city);
      if (user.state) setRepState(user.state);
      if (user.zip) setRepZip(user.zip);
    }
  }, [user]);

  useEffect(() => {
    loadPastRequests();
    // Default target date: 7 days from now
    const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    setTargetDate(d.toISOString().split('T')[0]);
  }, []);

  const dryRunParam = dryRun ? '?dryRun=true' : '';

  const loadPastRequests = async () => {
    try {
      const res = await authFetch('/api/porting/requests');
      if (res.ok) {
        const data = await res.json();
        setPastRequests(data.requests || []);
      }
    } catch (e) {
      // Silent fail for past requests
    }
  };

  // ──────────── Step 1: Check Portability ────────────

  const handleCheckPortability = async () => {
    setError('');
    setPortabilityResult(null);
    setLoading(true);

    try {
      const res = await fetch('/api/porting/check-portability' + dryRunParam, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to check portability');
        return;
      }

      setPortabilityResult(data);
      // Pre-fill carrier name from result
      if (data.carrierInfo?.name) {
        setCarrierName(data.carrierInfo.name);
        setDetectedCarrier(data.carrierInfo.name);
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ──────────── Step 3: Submit (Review & Sign LOA) ────────────

  const handleSubmitRequest = async () => {
    setError('');
    setLoading(true);

    try {
      const body = {
        phoneNumbers: [phoneNumber],
        carrierInfo: { name: carrierName },
        accountInfo: { accountNumber, pin: pin || undefined },
        authorizedRep: {
          name: repName, email: repEmail, phone: repPhone,
          address: repAddress, city: repCity, state: repState, zip: repZip,
        },
        loaSigned: true,
        loaSignedName: loaTypedName,
        targetDate,
      };

      if (dryRun) body.dryRun = true;

      const res = await authFetch('/api/porting/create-request' + dryRunParam, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to create port request');
        return;
      }

      setSubmitResult(data);
      setCurrentPortStatus('draft');
      setStep(4);
      if (!dryRun) loadPastRequests();
    } catch (e) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ──────────── Step 4: Simulate Progress ────────────

  const handleSimulateProgress = async () => {
    setSimulating(true);

    try {
      const res = await fetch('/api/porting/simulate-progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentStatus: currentPortStatus }),
      });

      const data = await res.json();
      if (data.success) {
        setCurrentPortStatus(data.currentStatus);
        // After dry-run completes, redirect to numbers dashboard
        if (data.currentStatus === 'completed') {
          setTimeout(() => navigate('/numbers'), 1500);
        }
      }
    } catch (e) {
      // Silent fail
    } finally {
      setSimulating(false);
    }
  };

  // ──────────── Profile save ────────────

  const saveProfileInfo = async () => {
    try {
      const [firstName, ...lastParts] = repName.trim().split(' ');
      const lastName = lastParts.join(' ') || '';
      await authFetch('/api/porting/update-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstname: firstName,
          lastname: lastName,
          address: repAddress,
          city: repCity,
          state: repState,
          zip: repZip,
        }),
      });
    } catch (e) {
      // Non-blocking — profile save is best-effort
    }
    setShowProfileDialog(false);
    setStep(3);
  };

  // ──────────── Navigation ────────────

  const canProceedStep1 = portabilityResult?.portable === true;
  const canProceedStep2 = carrierName && accountNumber && repName && repEmail;
  const canSubmitLoa = loaSigned && loaTypedName.trim().length > 0;
  const profileComplete = repName.trim() && repEmail.trim() && repAddress.trim() && repCity.trim() && repState.trim() && repZip.trim();

  const goNext = () => {
    if (step === 2 && !profileComplete) {
      setShowProfileDialog(true);
      return;
    }
    setStep(s => Math.min(s + 1, 4));
  };
  const goBack = () => setStep(s => Math.max(s - 1, 1));

  // ──────────── Render Steps ────────────

  const renderStep1 = () => (
    <div className="step-content">
      <h3>Check Number Portability</h3>
      <p className="step-description">
        Enter your current phone number to check if it can be ported to NumFree.
      </p>

      <div className="form-group">
        <label>Phone Number</label>
        <div className="phone-input-group">
          <input
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={phoneNumber}
            onChange={e => setPhoneNumber(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCheckPortability()}
          />
          <button
            className="check-btn"
            onClick={handleCheckPortability}
            disabled={!phoneNumber || loading}
          >
            {loading ? <><span className="loading-inline" />Checking...</> : 'Check Portability'}
          </button>
        </div>
        <span className="hint">US numbers only. Enter with or without country code.</span>
      </div>

      {error && <div className="wizard-error">{error}</div>}

      {portabilityResult && (
        <div className={`portability-result ${portabilityResult.portable ? 'portable' : 'not-portable'}`}>
          <h4>{portabilityResult.portable ? 'This number can be ported!' : 'This number cannot be ported'}</h4>

          <div className="carrier-info">
            <div className="info-item">
              <span className="info-label">Network:</span>
              <span className="info-value">{detectedCarrier}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Carrier:</span>
              <span className="info-value">{carrierName !== detectedCarrier ? <strong>{carrierName}</strong> : carrierName}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Type:</span>
              <span className="info-value">{portabilityResult.carrierInfo?.type}</span>
            </div>
            {portabilityResult.dryRun && (
              <div className="info-item">
                <span className="info-label">Source:</span>
                <span className="info-value"><span className="dry-run-badge">SIMULATED</span></span>
              </div>
            )}
          </div>

          {portabilityResult.portable && (
            <>
              {/* Show MVNO alternatives if a parent carrier was detected */}
              {CARRIER_FAMILY[detectedCarrier] && (
                <div className="carrier-alternatives">
                  <p><strong>Not on {detectedCarrier}?</strong> Your number may be on one of these networks that use the {detectedCarrier} network:</p>
                  <div className="alt-carrier-chips">
                    <button
                      className={`alt-carrier-chip ${carrierName === detectedCarrier ? 'selected' : ''}`}
                      onClick={() => setCarrierName(detectedCarrier)}
                    >
                      {detectedCarrier}
                    </button>
                    {CARRIER_FAMILY[detectedCarrier].map(alt => (
                      <button
                        key={alt}
                        className={`alt-carrier-chip ${carrierName === alt ? 'selected' : ''}`}
                        onClick={() => setCarrierName(alt)}
                      >
                        {alt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="requirements-list">
                <h5>Requirements for porting from {carrierName}:</h5>
                <ul>
                  <li>Account number from {carrierName}</li>
                  {portabilityResult.requiresPin && <li>Account PIN / Transfer PIN</li>}
                  <li>Letter of Authorization (LOA)</li>
                  <li>Authorized representative information</li>
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      <div className="step-navigation">
        <div />
        <button className="btn-next" disabled={!canProceedStep1} onClick={goNext}>
          Continue
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <h3>Carrier Account Information</h3>
      <p className="step-description">
        Provide your current carrier account details for the port request.
      </p>

      {error && <div className="wizard-error">{error}</div>}

      <div className="form-group">
        <label>Current Carrier</label>
        <select
          value={carrierName}
          onChange={e => setCarrierName(e.target.value)}
        >
          <option value="">Select your carrier...</option>
          {US_CARRIERS.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
          {carrierName && !US_CARRIERS.includes(carrierName) && (
            <option value={carrierName}>{carrierName} (detected)</option>
          )}
          <option value="__other">Other...</option>
        </select>
        {carrierName === '__other' && (
          <input
            type="text"
            style={{ marginTop: '8px' }}
            placeholder="Enter carrier name"
            autoFocus
            onChange={e => setCarrierName(e.target.value)}
          />
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Account Number</label>
          <input
            type="text"
            value={accountNumber}
            onChange={e => setAccountNumber(e.target.value)}
            placeholder="Your carrier account number"
          />
        </div>
        {portabilityResult?.requiresPin && (
          <div className="form-group">
            <label>Transfer PIN</label>
            <input
              type="text"
              value={pin}
              onChange={e => setPin(e.target.value)}
              placeholder="Account PIN"
            />
            <span className="hint">Required by {carrierName}</span>
          </div>
        )}
      </div>

      {carrierName && CARRIER_INFO[carrierName] && (
        <div className="carrier-help-box">
          <h5>Where to find your {carrierName} info</h5>
          <div className="carrier-help-item">
            <strong>Account Number:</strong> {CARRIER_INFO[carrierName].acctHelp}
          </div>
          {portabilityResult?.requiresPin && (
            <div className="carrier-help-item">
              <strong>Transfer PIN:</strong> {CARRIER_INFO[carrierName].pinHelp}
            </div>
          )}
        </div>
      )}

      <h4 style={{ fontSize: '16px', margin: '20px 0 12px', color: '#374151' }}>
        Authorized Representative
      </h4>

      <div className="form-group">
        <label>Full Name</label>
        <input
          type="text"
          value={repName}
          onChange={e => setRepName(e.target.value)}
          placeholder="Name on the account"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={repEmail}
            onChange={e => setRepEmail(e.target.value)}
            placeholder="Contact email"
          />
        </div>
        <div className="form-group">
          <label>Phone (optional)</label>
          <input
            type="tel"
            value={repPhone}
            onChange={e => setRepPhone(e.target.value)}
            placeholder="Contact phone"
          />
        </div>
      </div>

      <div className="step-navigation">
        <button className="btn-back" onClick={goBack}>Back</button>
        <button className="btn-next" disabled={!canProceedStep2} onClick={goNext}>
          Continue
        </button>
      </div>
    </div>
  );

  const todayStr = new Date().toISOString().split('T')[0];

  const renderStep3 = () => (
    <div className="step-content">
      <h3>Review & Sign LOA</h3>
      <p className="step-description">
        Review your port request and sign the Letter of Authorization (LOA) required by the FCC to transfer your number.
      </p>

      {error && <div className="wizard-error">{error}</div>}

      <div className="review-summary">
        <div className="review-section">
          <h4>Phone Number</h4>
          <p className="review-value">{phoneNumber}</p>
          <p>Current carrier: <span className="review-value">{carrierName}</span></p>
        </div>

        <div className="review-section">
          <h4>Carrier Account</h4>
          <p>Account: <span className="review-value">{accountNumber}</span></p>
          {pin && <p>PIN: <span className="review-value">{'*'.repeat(pin.length)}</span></p>}
        </div>

        <div className="review-section">
          <h4>Authorized Representative</h4>
          <p className="review-value">{repName}</p>
          <p>{repEmail}{repPhone ? ` | ${repPhone}` : ''}</p>
        </div>

        <div className="review-section">
          <h4>Target Porting Date</h4>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              min={todayStr}
            />
          </div>
        </div>
      </div>

      <div className="loa-document">
        <h4>Letter of Authorization</h4>
        <div className="loa-text">
          <p>
            I, <strong>{repName || '_______________'}</strong>, hereby authorize NumFree to
            port the following telephone number(s) from <strong>{carrierName || '_______________'}</strong> to
            NumFree's service:
          </p>
          <p className="loa-number">{phoneNumber}</p>
          <p>
            Account Number: <strong>{accountNumber || '_______________'}</strong>
          </p>
          <p>
            Service Address: <strong>{[repAddress, repCity, repState, repZip].filter(Boolean).join(', ') || '_______________'}</strong>
          </p>
          <p>
            I confirm that I am the authorized account holder or have been duly authorized
            by the account holder to submit this request. I understand that:
          </p>
          <ul>
            <li>My current service with {carrierName || 'my carrier'} for this number will be terminated upon successful porting.</li>
            <li>I may be subject to early termination fees from {carrierName || 'my carrier'} if I am under contract.</li>
            <li>The porting process typically takes 1-3 business days for wireless numbers.</li>
            <li>I will maintain my current service active until the port is complete.</li>
          </ul>
          <p>
            Contact: {repEmail}{repPhone ? `, ${repPhone}` : ''}
          </p>
          <p className="loa-date">
            Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="loa-signature">
          <div className="form-group">
            <label>Type your full name to sign</label>
            <input
              type="text"
              className="signature-input"
              value={loaTypedName}
              onChange={e => setLoaTypedName(e.target.value)}
              placeholder="Type your full legal name"
            />
          </div>
          <label className="loa-checkbox">
            <input
              type="checkbox"
              checked={loaSigned}
              onChange={e => setLoaSigned(e.target.checked)}
            />
            <span>I have read and agree to the above Letter of Authorization</span>
          </label>
        </div>
      </div>

      <div className="step-navigation">
        <button className="btn-back" onClick={goBack}>Back</button>
        <button
          className="btn-submit"
          onClick={handleSubmitRequest}
          disabled={loading || !canSubmitLoa}
        >
          {loading
            ? <><span className="loading-inline" />Submitting...</>
            : dryRun ? 'Submit (Dry Run)' : 'Submit Port Request'
          }
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const currentIdx = STATUS_STEPS.findIndex(s => s.status === currentPortStatus);

    return (
      <div className="step-content">
        <h3>Port Request Status</h3>
        {submitResult && (
          <div className="wizard-success">
            Port request {submitResult.dryRun ? '(simulated) ' : ''}created successfully!
            Request ID: <strong>{submitResult.portInRequestSid || submitResult.requestId}</strong>
          </div>
        )}

        <div className="status-timeline">
          {STATUS_STEPS.map((s, idx) => {
            let state = 'pending';
            if (idx < currentIdx) state = 'completed';
            else if (idx === currentIdx) state = 'active';

            return (
              <div className={`timeline-step ${state}`} key={s.status}>
                <div className="timeline-icon">
                  {state === 'completed' ? '\u2713' : idx + 1}
                </div>
                <div className="timeline-content">
                  <h4>{s.label}</h4>
                  <p>
                    {state === 'completed' && 'Completed'}
                    {state === 'active' && 'In progress...'}
                    {state === 'pending' && 'Waiting'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {dryRun && (
          <>
            <button
              className="simulate-btn"
              onClick={handleSimulateProgress}
              disabled={simulating || currentPortStatus === 'completed'}
            >
              {simulating
                ? 'Advancing...'
                : currentPortStatus === 'completed'
                  ? 'Redirecting to Numbers...'
                  : 'Simulate Next Step'
              }
            </button>
            {currentPortStatus === 'completed' && (
              <p style={{ marginTop: '12px', color: '#64748b', fontSize: '14px' }}>
                Redirecting to your numbers dashboard...
              </p>
            )}
          </>
        )}

        <div className="step-navigation" style={{ marginTop: '24px' }}>
          <button className="btn-back" onClick={() => {
            // Reset wizard for a new request
            setStep(1);
            setPortabilityResult(null);
            setPhoneNumber('');
            setCarrierName('');
            setDetectedCarrier('');
            setAccountNumber('');
            setPin('');
            setRepName('');
            setRepEmail('');
            setRepPhone('');
            setLoaSigned(false);
            setLoaTypedName(repName); // keep name for next request
            setSubmitResult(null);
            setCurrentPortStatus('draft');
            setError('');
          }}>
            Start New Request
          </button>
          <div />
        </div>
      </div>
    );
  };

  // ──────────── Main Render ────────────

  return (
    <div className={`porting-wizard ${dryRun ? 'dry-run-active' : ''}`}>
      <div className="porting-header">
        <h2>Port Your Number</h2>
        <div className="dry-run-toggle">
          <label onClick={() => setDryRun(!dryRun)}>
            <div className={`toggle-switch ${dryRun ? 'active' : ''}`} />
            <span>Dry Run Mode</span>
          </label>
          {dryRun && <span className="dry-run-badge">SIMULATION</span>}
        </div>
      </div>

      {dryRun && (
        <div className="dry-run-banner">
          <strong>Dry Run Mode Active</strong> &mdash; No data will be saved or sent to carriers. Walk through the entire porting flow with simulated responses.
        </div>
      )}

      {/* Step Indicator */}
      <div className="step-indicator">
        {STEPS.map((s, idx) => (
          <React.Fragment key={s.id}>
            <div className={`step-dot ${step === s.id ? 'active' : ''} ${step > s.id ? 'completed' : ''}`}>
              <div className={`step-circle ${step === s.id ? 'active' : ''} ${step > s.id ? 'completed' : ''}`}>
                {step > s.id ? '\u2713' : s.id}
              </div>
              <span className="step-label">{s.label}</span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`step-connector ${step > s.id ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      {/* Profile Completion Dialog */}
      {showProfileDialog && (
        <div className="profile-dialog-overlay" onClick={() => setShowProfileDialog(false)}>
          <div className="profile-dialog" onClick={e => e.stopPropagation()}>
            <h3>Complete Your Information</h3>
            <p className="step-description">
              The LOA requires your full name and service address. Please fill in any missing details.
            </p>

            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={repName}
                onChange={e => { setRepName(e.target.value); setLoaTypedName(e.target.value); }}
                placeholder="Your full legal name"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={repEmail}
                onChange={e => setRepEmail(e.target.value)}
                placeholder="Contact email"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone (alternative)</label>
                <input
                  type="tel"
                  value={repPhone}
                  onChange={e => setRepPhone(e.target.value)}
                  placeholder="Alternative phone number"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Street Address *</label>
              <input
                type="text"
                value={repAddress}
                onChange={e => setRepAddress(e.target.value)}
                placeholder="123 Main St, Apt 4"
              />
            </div>

            <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={repCity}
                  onChange={e => setRepCity(e.target.value)}
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  value={repState}
                  onChange={e => setRepState(e.target.value)}
                  placeholder="CA"
                  maxLength={2}
                />
              </div>
              <div className="form-group">
                <label>ZIP *</label>
                <input
                  type="text"
                  value={repZip}
                  onChange={e => setRepZip(e.target.value)}
                  placeholder="90210"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="profile-dialog-actions">
              <button className="btn-back" onClick={() => setShowProfileDialog(false)}>Cancel</button>
              <button
                className="btn-next"
                disabled={!repName.trim() || !repEmail.trim() || !repAddress.trim() || !repCity.trim() || !repState.trim() || !repZip.trim()}
                onClick={saveProfileInfo}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Past Requests */}
      {step === 1 && (
        <div className="past-requests">
          <h3>Your Port Requests</h3>
          {pastRequests.length === 0 ? (
            <div className="empty-requests">
              No port requests yet. Start by checking a number above.
            </div>
          ) : (
            pastRequests.map(req => (
              <div className="request-card" key={req._id || req.portInRequestSid}>
                <div className="request-info">
                  <span className="request-number">
                    {req.phoneNumbers ? req.phoneNumbers.join(', ') : 'Unknown'}
                  </span>
                  <span className="request-date">
                    {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : ''}
                  </span>
                </div>
                <span className={`status-badge ${req.status || 'draft'}`}>
                  {(req.status || 'draft').replace('_', ' ')}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PortingWizard;
